import { useEffect, useState } from 'react';
import { Button, Checkbox, Group, Select, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEdit } from '@tabler/icons-react';
import type { TrxStatus } from '../api/types';
import { useTranslation } from '../i18n/LocaleContext';

const ALL_STATUSES: readonly TrxStatus[] = ['pending', 'paid', 'failed'];

/**
 * Manual status override control shown on the deposit / disbursement detail
 * pages (internal staff only — the server gates on `*.status.update`).
 *
 *   [ Ubah status ke… ▾ ]  [x] Kirim ulang callback   [ Update status ]
 *
 * Picking a target status and pressing the button opens a confirm modal that
 * spells out the transition + whether the merchant callback will be re-sent.
 * `paid → pending` is not offered: the server rejects it (the operator must go
 * through `failed` so the wallet reversal is explicit). The parent owns the
 * mutation; this component only collects the intent.
 */
export function ManualStatusControl({
  trxId,
  currentStatus,
  loading,
  onConfirm,
}: {
  trxId: string;
  currentStatus: TrxStatus | undefined;
  loading: boolean;
  onConfirm: (input: { status: TrxStatus; resendCallback: boolean }) => void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState<TrxStatus | null>(null);
  const [resendCallback, setResendCallback] = useState(false);

  // A successful update changes currentStatus → clear the stale selection.
  useEffect(() => {
    setTarget(null);
  }, [currentStatus]);

  const options = ALL_STATUSES.filter(
    (s) => s !== currentStatus && !(currentStatus === 'paid' && s === 'pending'),
  ).map((s) => ({ value: s, label: s }));

  const openConfirm = () => {
    if (!target || !currentStatus) return;
    const event = target === 'paid' || target === 'failed' ? target : null;
    modals.openConfirmModal({
      title: t('trxStatus.confirmTitle'),
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            {t('trxStatus.confirmBody', { trxId, from: currentStatus, to: target })}
          </Text>
          <Text size="sm" fw={600}>
            {resendCallback && event
              ? t('trxStatus.confirmCallbackOn', { status: event })
              : resendCallback
                ? t('trxStatus.confirmCallbackNoEvent')
                : t('trxStatus.confirmCallbackOff')}
          </Text>
        </Stack>
      ),
      labels: { confirm: t('trxStatus.confirmYes'), cancel: t('common.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => onConfirm({ status: target, resendCallback }),
    });
  };

  return (
    <Group gap="xs" wrap="nowrap">
      <Select
        size="sm"
        w={170}
        placeholder={t('trxStatus.selectPlaceholder')}
        data={options}
        value={target}
        onChange={(v) => setTarget((v as TrxStatus | null) ?? null)}
        disabled={!currentStatus || loading}
        allowDeselect
        aria-label={t('trxStatus.selectPlaceholder')}
      />
      <Checkbox
        size="sm"
        label={t('trxStatus.resendCallback')}
        checked={resendCallback}
        onChange={(e) => setResendCallback(e.currentTarget.checked)}
        disabled={!currentStatus || loading}
      />
      <Button
        size="sm"
        color="orange"
        variant="light"
        leftSection={<IconEdit size={14} />}
        onClick={openConfirm}
        loading={loading}
        disabled={!target || !currentStatus}
      >
        {t('trxStatus.updateButton')}
      </Button>
    </Group>
  );
}
